import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, Sun, Moon, Compass, ArrowLeft, Heart, ChevronRight, Music, Star, Briefcase, Users, Hash } from "lucide-react";
import { getPaymentStatus, getBirthChartReading, getBirthChartWheelUrl } from "@/lib/api";
import BirthChartShareCard from "@/components/earthpulse/BirthChartShareCard";
import SocialShareButtons from "@/components/earthpulse/SocialShareButtons";
import { useReadingGate } from "@/components/earthpulse/ReadingGate";
import { trackPurchase } from "@/lib/tracking";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 30;

function DeepCard({ label, body, accent, testid }) {
  const color = accent === "rose" ? "var(--rose-gold)" : "var(--gold)";
  return (
    <div className="parchment-card rounded-2xl p-8 md:p-10" data-testid={testid}>
      <div className="label-overline" style={{ color }}>{label}</div>
      <p className="mt-5 text-[color:var(--parchment-soft)] leading-relaxed font-sans-body text-base md:text-lg">
        {body}
      </p>
    </div>
  );
}


function Section({ icon: Icon, label, sign, degree, element, modality, reading, color }) {
  if (!sign) return null;
  return (
    <div className="parchment-card rounded-2xl p-8 md:p-10 stagger-in">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Icon size={18} className="text-[color:var(--gold)]" />
          <div className="label-overline">{label}</div>
        </div>
        <div className="text-right">
          <div className="label-overline">{element} · {modality}</div>
          <div className="font-serif-italic text-[color:var(--text-secondary)] text-sm">
            {degree?.toFixed?.(1) ?? degree}°
          </div>
        </div>
      </div>
      <h2
        className="font-serif-display text-4xl md:text-6xl tracking-tight leading-none"
        style={{ color }}
      >
        {sign}
      </h2>
      {reading && (
        <p className="mt-5 text-[color:var(--parchment-soft)] text-base md:text-lg font-sans-body leading-relaxed">
          {reading}
        </p>
      )}
    </div>
  );
}

export default function BirthChartReading() {
  const gateEl = useReadingGate("birth chart reading");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | ready | error
  const [reading, setReading] = useState(null);
  const [error, setError] = useState(null);
  const purchaseFiredRef = useRef(false);

  const load = useCallback(async () => {
    if (!sessionId) return;
    let attempts = 0;
    while (attempts < MAX_POLLS) {
      try {
        const s = await getPaymentStatus(sessionId);
        if (s.payment_status === "paid") {
          const r = await getBirthChartReading(sessionId);
          setReading(r);
          setState("ready");
          // Fire conversion event once per successful load (idempotent).
          if (!purchaseFiredRef.current) {
            purchaseFiredRef.current = true;
            const value = (r?.pricing?.amount_pence ?? 500) / 100;
            trackPurchase({ value, contentId: "cosmic_birth_chart" });
          }
          return;
        }
        if (["failed", "expired"].includes(s.payment_status)) {
          setError(`Payment ${s.payment_status}. Please try again.`);
          setState("error");
          return;
        }
      } catch (e) {
        // 404 (unknown session) — bail out immediately instead of polling for a minute.
        if (e?.response?.status === 404) {
          setError("This reading session doesn't exist or has expired.");
          setState("error");
          return;
        }
        // Other errors — retry
      }
      attempts += 1;
      await new Promise((r) => setTimeout(r, POLL_INTERVAL));
    }
    setError("Timed out confirming payment. If you were charged, refresh in a moment.");
    setState("error");
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  // Verify £8.88/month membership BEFORE anything else — if the visitor
  // isn't a subscriber, `gateEl` is the paywall / splash to render.
  if (gateEl) return gateEl;

  if (!sessionId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="font-serif-italic text-[color:var(--parchment-soft)] mb-4">No reading session found.</p>
          <Link to="/birth-chart" className="text-[color:var(--gold)] hover:underline">Start a new reading</Link>
        </div>
      </div>
    );
  }

  if (state === "polling") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="mx-auto w-14 h-14 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center animate-pulse">
            <Sparkles size={22} className="text-[color:var(--gold)]" />
          </div>
          <h1 className="mt-8 font-serif-display text-3xl md:text-4xl text-[color:var(--parchment)] tracking-tight">
            Reading the sky for you…
          </h1>
          <p className="mt-4 font-serif-italic text-[color:var(--text-secondary)]">
            Confirming payment and calculating your placements. This takes a few seconds.
          </p>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Sparkles size={22} className="text-[color:var(--gold)] mx-auto mb-4" />
          <h1 className="font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl mb-3" data-testid="reading-error-title">
            We couldn&apos;t open this reading.
          </h1>
          <p className="font-serif-italic text-[color:var(--parchment-soft)] mb-6" data-testid="reading-error">
            {error}
          </p>
          <div className="space-y-3">
            <Link
              to="/my-readings"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition"
              data-testid="reading-error-resend"
            >
              Re-send my reading links
            </Link>
            <Link
              to="/birth-chart"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[color:var(--gold)]/40 text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10 transition"
            >
              Start a new reading <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const chart = reading?.chart || {};
  const r = reading?.reading || {};
  const name = reading?.birth_data?.name;

  return (
    <div
      className="min-h-screen relative z-10"
      data-testid="reading-page"
    >
      <header className="border-b border-[color:var(--gold)]/15">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/almanac" className="flex items-center gap-3 group">
            <ArrowLeft size={16} className="text-[color:var(--gold)] group-hover:-translate-x-0.5 transition-transform" />
            <span className="label-overline">Back to Guidance</span>
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[color:var(--gold)]" />
            <span className="font-serif-display text-white text-lg">Your Birth Chart</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 md:px-10 py-14 md:py-20 space-y-10">
        <section className="text-center max-w-3xl mx-auto stagger-in">
          <div className="label-overline">A reading for {name || "you"}</div>
          <h1 className="mt-5 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.05] tracking-tight">
            {r.essence || "The stars have arranged something for you."}
          </h1>
          {reading?.birth_data?.email && (
            <div
              className="mt-8 mx-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[color:var(--gold)]/40 font-serif-italic text-[color:var(--parchment-soft)]"
              style={{ background: "rgba(212,162,76,0.06)" }}
              data-testid="email-sent-notice"
            >
              <Sparkles size={14} className="text-[color:var(--gold)]" />
              {reading.email_sent
                ? `A copy has been sent to ${reading.birth_data.email}.`
                : `We'll email a copy to ${reading.birth_data.email} shortly.`}
            </div>
          )}
          <div className="divider-ornate mt-10 font-serif-italic">
            <Sparkles size={14} />
          </div>
        </section>

        {/* Life theme — the overarching story */}
        {r.life_theme && (
          <section className="parchment-card rounded-2xl p-8 md:p-12 stagger-in" data-testid="life-theme-block">
            <div className="label-overline">The overarching story</div>
            <h2 className="mt-4 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl leading-snug tracking-tight">
              Your life theme
            </h2>
            <p className="mt-6 text-[color:var(--parchment-soft)] text-lg leading-relaxed font-sans-body">
              {r.life_theme}
            </p>
          </section>
        )}

        <Section
          icon={Sun}
          label="Sun · your core self"
          sign={chart.sun?.sign}
          degree={chart.sun?.degree}
          element={chart.sun?.element}
          modality={chart.sun?.modality}
          reading={r.sun_reading}
          color="var(--gold)"
        />

        <Section
          icon={Moon}
          label="Moon · your inner world"
          sign={chart.moon?.sign}
          degree={chart.moon?.degree}
          element={chart.moon?.element}
          modality={chart.moon?.modality}
          reading={r.moon_reading}
          color="var(--rose-gold)"
        />

        <Section
          icon={Compass}
          label="Rising · how you meet the world"
          sign={chart.rising?.sign}
          degree={chart.rising?.degree}
          element={chart.rising?.element}
          modality={chart.rising?.modality}
          reading={r.rising_reading}
          color="var(--parchment)"
        />

        {(!chart.moon || !chart.rising) && (
          <div className="text-center font-serif-italic text-[color:var(--text-secondary)] text-sm">
            Add birth time {(!chart.rising) && "and location "}for a fuller reading next time.
          </div>
        )}

        {/* ─────────── Natal wheel: printable keepsake ─────────── */}
        <section className="stagger-in">
          <div className="text-center mb-6">
            <div className="label-overline">Your natal wheel</div>
            <h2 className="mt-3 font-serif-display text-[color:var(--parchment)] text-3xl md:text-4xl tracking-tight">
              A snapshot of the sky the moment you arrived.
            </h2>
            <p className="mt-3 font-serif-italic text-[color:var(--text-secondary)] max-w-md mx-auto">
              A moment in time that will never happen again — screenshot it to keep.
            </p>
          </div>
          <div className="parchment-card rounded-2xl p-4 md:p-8 flex flex-col items-center gap-6" data-testid="natal-wheel-block">
            <img
              src={getBirthChartWheelUrl(sessionId)}
              alt="Your natal birth chart wheel"
              className="w-full max-w-[520px] rounded-xl"
              data-testid="natal-wheel-image"
            />
          </div>
        </section>

        {/* ─────────── Deep dives: love language, communication, blooming, slowing down ─────────── */}
        {(r.love_language || r.communication_style || r.where_you_bloom || r.where_you_slow_down) && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger-in" data-testid="deep-dives-block">
            {r.love_language && (
              <DeepCard label="How you love" body={r.love_language} accent="rose" testid="love-language-card" />
            )}
            {r.communication_style && (
              <DeepCard label="How you think & speak" body={r.communication_style} accent="gold" testid="communication-card" />
            )}
            {r.where_you_bloom && (
              <DeepCard label="Where you bloom" body={r.where_you_bloom} accent="gold" testid="bloom-card" />
            )}
            {r.where_you_slow_down && (
              <DeepCard label="Where you slow down" body={r.where_you_slow_down} accent="rose" testid="slow-down-card" />
            )}
          </section>
        )}

        {/* ─────────── Soul purpose (North Node) ─────────── */}
        {r.soul_purpose && (
          <section className="parchment-card rounded-2xl p-8 md:p-12 stagger-in text-center" data-testid="soul-purpose-block">
            <Sparkles size={20} className="mx-auto text-[color:var(--gold)]" />
            <div className="mt-4 label-overline">Your soul&apos;s direction</div>
            <h2 className="mt-3 font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl tracking-tight">
              Where you&apos;re heading
            </h2>
            {chart?.planets?.["North Node"] && (
              <p className="mt-2 font-serif-italic text-[color:var(--rose-gold)] text-sm">
                North Node in {chart.planets["North Node"].sign}
              </p>
            )}
            <p className="mt-6 max-w-3xl mx-auto text-[color:var(--parchment-soft)] text-lg leading-relaxed font-sans-body text-left md:text-center">
              {r.soul_purpose}
            </p>
          </section>
        )}

        {/* ─────────── Chinese zodiac interlude ─────────── */}
        {chart?.chinese_zodiac && r.chinese_zodiac_note && (
          <section className="parchment-card rounded-2xl p-8 md:p-10 stagger-in" data-testid="chinese-zodiac-block">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
              <div className="label-overline">Chinese zodiac · East meets West</div>
              <span className="font-serif-italic text-[color:var(--text-secondary)] text-sm">
                born in the year of the {chart.chinese_zodiac.element} {chart.chinese_zodiac.animal} ({chart.chinese_zodiac.year})
              </span>
            </div>
            <p className="mt-5 text-[color:var(--parchment-soft)] leading-relaxed font-sans-body">
              {r.chinese_zodiac_note}
            </p>
          </section>
        )}
        {Array.isArray(r.notable_people) && r.notable_people.length > 0 && (
          <section className="parchment-card rounded-2xl p-8 md:p-10 stagger-in" data-testid="notable-people-block">
            <div className="flex items-center gap-3 mb-5">
              <Users size={16} className="text-[color:var(--gold)]" />
              <div className="label-overline">Born on your day</div>
            </div>
            <p className="font-serif-italic text-[color:var(--text-secondary)] mb-6">
              You share a birthday with —
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {r.notable_people.map((p, i) => (
                <li key={i} className="flex gap-3">
                  <ChevronRight size={14} className="mt-1.5 text-[color:var(--gold)] shrink-0" />
                  <div>
                    <div className="font-serif-display text-[color:var(--parchment)] text-lg">{p.name}</div>
                    <div className="font-serif-italic text-[color:var(--text-secondary)] text-sm">{p.note}</div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* ─────────── #1 song on your birthday ─────────── */}
        {r.number_one_song && r.number_one_song.title && (
          <section className="parchment-card rounded-2xl p-8 md:p-10 stagger-in text-center" data-testid="number-one-song-block">
            <Music size={18} className="mx-auto text-[color:var(--rose-gold)]" />
            <div className="mt-4 label-overline">The soundtrack of your arrival</div>
            <p className="mt-6 font-serif-display text-[color:var(--parchment)] text-3xl md:text-4xl tracking-tight leading-snug">
              &ldquo;{r.number_one_song.title}&rdquo;
            </p>
            <p className="mt-3 font-serif-italic text-[color:var(--rose-gold)] text-lg">
              {r.number_one_song.artist}
            </p>
            <p className="mt-4 font-serif-italic text-[color:var(--text-muted)] text-xs">
              {r.number_one_song.chart || "UK Singles Chart"} · {r.number_one_song.date_context || ""}
            </p>
          </section>
        )}

        {/* ─────────── Lucky numbers, colours, power days ─────────── */}
        {(chart.lucky_numbers?.length || r.lucky_colours?.length || r.power_days?.length || chart.life_path) && (
          <section className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-in" data-testid="lucky-block">
            {chart.lucky_numbers?.length > 0 && (
              <div className="parchment-card rounded-2xl p-8 text-center">
                <Hash size={16} className="mx-auto text-[color:var(--gold)]" />
                <div className="label-overline mt-3">Lucky numbers</div>
                <div className="mt-5 flex justify-center flex-wrap gap-3">
                  {chart.lucky_numbers.map((n) => (
                    <div
                      key={n}
                      className="w-12 h-12 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center font-serif-display text-[color:var(--parchment)] text-xl"
                    >
                      {n}
                    </div>
                  ))}
                </div>
                {chart.life_path && (
                  <p className="mt-4 font-serif-italic text-[color:var(--text-muted)] text-xs">
                    Life-path number: {chart.life_path}
                  </p>
                )}
              </div>
            )}
            {r.lucky_colours?.length > 0 && (
              <div className="parchment-card rounded-2xl p-8 text-center">
                <Star size={16} className="mx-auto text-[color:var(--rose-gold)]" />
                <div className="label-overline mt-3">Colours that lift you</div>
                <ul className="mt-5 space-y-2">
                  {r.lucky_colours.map((c, i) => (
                    <li key={i} className="font-serif-display text-[color:var(--parchment)] text-lg capitalize">{c}</li>
                  ))}
                </ul>
              </div>
            )}
            {r.power_days?.length > 0 && (
              <div className="parchment-card rounded-2xl p-8 text-center">
                <Sparkles size={16} className="mx-auto text-[color:var(--gold)]" />
                <div className="label-overline mt-3">Power days</div>
                <ul className="mt-5 space-y-2">
                  {r.power_days.map((d, i) => (
                    <li key={i} className="font-serif-italic text-[color:var(--parchment-soft)]">{d}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* ─────────── Career paths ─────────── */}
        {Array.isArray(r.career_paths) && r.career_paths.length > 0 && (
          <section className="parchment-card rounded-2xl p-8 md:p-10 stagger-in" data-testid="career-paths-block">
            <div className="flex items-center gap-3 mb-5">
              <Briefcase size={16} className="text-[color:var(--gold)]" />
              <div className="label-overline">Callings that suit your chart</div>
            </div>
            <p className="font-serif-italic text-[color:var(--text-secondary)] mb-6">
              Not fate — pattern. Roles where these placements tend to feel most at home.
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {r.career_paths.map((c, i) => (
                <li key={i} className="flex items-start gap-3 font-serif-display text-[color:var(--parchment)] text-lg">
                  <ChevronRight size={14} className="mt-2 text-[color:var(--gold)] shrink-0" /> {c}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger-in">
          <div className="parchment-card rounded-2xl p-8">
            <div className="label-overline text-[color:var(--gold)] constellation-underline">Gifts</div>
            <ul className="mt-5 space-y-3">
              {(r.gifts || []).map((g, i) => (
                <li key={i} className="flex items-start gap-3 text-[color:var(--parchment-soft)] font-sans-body">
                  <ChevronRight size={14} className="mt-1.5 text-[color:var(--gold)]" /> {g}
                </li>
              ))}
            </ul>
          </div>
          <div className="parchment-card rounded-2xl p-8">
            <div className="label-overline text-[color:var(--rose-gold)] constellation-underline">Growth edges</div>
            <ul className="mt-5 space-y-3">
              {(r.shadows || []).map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-[color:var(--parchment-soft)] font-sans-body">
                  <ChevronRight size={14} className="mt-1.5 text-[color:var(--rose-gold)]" /> {s}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {r.affirmation && (
          <section className="text-center max-w-2xl mx-auto stagger-in py-10">
            <Heart size={16} className="mx-auto text-[color:var(--gold)]" />
            <p
              className="mt-6 font-serif-display text-2xl md:text-4xl text-[color:var(--parchment)] tracking-tight leading-snug"
              data-testid="reading-affirmation"
            >
              &ldquo;{r.affirmation}&rdquo;
            </p>
          </section>
        )}

        {/* ─────────── A letter from the universe — the emotional payoff ─────────── */}
        {r.letter_from_the_universe && (
          <section
            className="parchment-card rounded-2xl p-8 md:p-14 stagger-in relative overflow-hidden"
            style={{ background: "linear-gradient(160deg, rgba(212,162,76,0.08), rgba(229,168,124,0.04))" }}
            data-testid="letter-from-universe"
          >
            <div className="text-center">
              <div className="label-overline">A letter, from the universe</div>
              <h2 className="mt-4 font-serif-display text-[color:var(--parchment)] text-3xl md:text-4xl tracking-tight">
                To {reading?.birth_data?.name || "you"},
              </h2>
            </div>
            <p className="mt-8 text-[color:var(--parchment-soft)] text-lg md:text-xl leading-relaxed font-serif-italic max-w-2xl mx-auto">
              {r.letter_from_the_universe}
            </p>
            <p className="mt-8 text-right font-serif-italic text-[color:var(--gold)]">
              — the stars
            </p>
          </section>
        )}

        <section className="text-center py-6 stagger-in">
          <div className="label-overline mb-3">Share this reading</div>
          <p className="font-serif-italic text-[color:var(--text-secondary)] mb-6 max-w-md mx-auto">
            A little card you can post to TikTok, Instagram, or send to a friend.
          </p>
          <BirthChartShareCard reading={reading} />
          <div className="mt-8">
            <SocialShareButtons
              url="https://earthpulse.store/"
              text={`I just read my Cosmic Birth Chart on Earth Pulse — my ${reading?.reading?.sun_sign || "sun"} came alive in ways I didn't expect. ✨`}
              testIdPrefix="birth-share"
            />
          </div>
        </section>

        <div className="pt-6 border-t border-[color:var(--gold)]/15 flex flex-wrap items-center justify-between gap-4">
          <Link
            to="/compatibility"
            className="inline-flex items-center gap-2 font-serif-italic text-[color:var(--rose-gold)] hover:underline"
            data-testid="compat-upsell"
          >
            <Heart size={14} /> See how your chart meets someone else&apos;s
          </Link>
          <Link
            to="/almanac"
            className="inline-flex items-center gap-2 font-serif-italic text-[color:var(--gold)] hover:underline"
          >
            Return to daily guidance <ChevronRight size={14} />
          </Link>
        </div>
      </main>
    </div>
  );
}
